import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Button, 
  Card, 
  CardMedia, 
  CardContent,
  Skeleton,
  Breadcrumbs,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Checkbox,
  DialogTitle,
  DialogActions,
  Tooltip,
  Badge,
  CircularProgress
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Event as EventIcon,
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  SelectAll as SelectAllIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

import { useAuth } from '../../components/auth/AuthModule';
import EditGalleryModal from './components/EditGalleryModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { gallery as galleryApi } from '../../utils/api';
import { getTransformedImageUrl } from '../../utils/filestack';

const GalleryDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('gallery');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // New state for multi-selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openDeleteImagesModal, setOpenDeleteImagesModal] = useState(false);
  
  // Touch swipe handling for mobile devices
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  
  // Minimum swipe distance required (in px)
  const minSwipeDistance = 50;
  
  // Fetch gallery details
  const fetchGallery = async () => {
    try {
      setLoading(true);
      const data = await galleryApi.getById(id);
      setGallery(data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error(error.message || 'Failed to load gallery');
      navigate('/galleries');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchGallery();
  }, [id]);
  
  const handleOpenEditModal = () => {
    setOpenEditModal(true);
  };
  
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
  };
  
  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true);
  };
  
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };
  
  const handleGalleryUpdated = () => {
    fetchGallery();
    handleCloseEditModal();
    toast.success(t('messages.updateSuccess'));
  };
  
  const handleGalleryDeleted = () => {
    navigate('/galleries');
    toast.success(t('messages.deleteSuccess'));
  };
  
  const handleOpenImage = (index) => {
    if (selectionMode) {
      handleToggleImageSelection(index);
    } else {
      setSelectedImageIndex(index);
      setOpenImageDialog(true);
    }
  };
  
  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
  };
  
  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => 
      prev === 0 ? gallery.images.length - 1 : prev - 1
    );
  };
  
  const handleNextImage = () => {
    setSelectedImageIndex((prev) => 
      prev === gallery.images.length - 1 ? 0 : prev + 1
    );
  };
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setSwipeDirection(null);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    
    if (touchStart && e.targetTouches[0].clientX) {
      const distance = touchStart - e.targetTouches[0].clientX;
      if (Math.abs(distance) > minSwipeDistance / 2) {
        setSwipeDirection(distance > 0 ? 'left' : 'right');
      } else {
        setSwipeDirection(null);
      }
    }
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNextImage();
    } else if (isRightSwipe) {
      handlePrevImage();
    }
    
    setSwipeDirection(null);
  };
  
  // Handle keyboard events for image dialog
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!openImageDialog) return;
      
      switch (e.key) {
        case 'Escape':
          handleCloseImageDialog();
          break;
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'ArrowRight':
          handleNextImage();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openImageDialog, gallery]);
  
  // New handlers for multi-selection
  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedImages([]);
  };
  
  const handleToggleImageSelection = (index) => {
    setSelectedImages(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (gallery?.images?.length) {
      const allIndices = Array.from({ length: gallery.images.length }, (_, i) => i);
      setSelectedImages(allIndices);
    }
  };
  
  const handleClearSelection = () => {
    setSelectedImages([]);
  };
  
  const handleOpenDeleteImagesModal = () => {
    if (selectedImages.length > 0) {
      setOpenDeleteImagesModal(true);
    }
  };
  
  const handleCloseDeleteImagesModal = () => {
    setOpenDeleteImagesModal(false);
  };
  
  const handleDeleteSelectedImages = async () => {
    try {
      setLoading(true);
      
      // Get the image handles/URLs to delete
      const imagesToDelete = selectedImages.map(index => {
        const image = gallery.images[index];
        return image.handle || image.url;
      });
      
      // Filter out the selected images
      const updatedImages = gallery.images.filter((_, index) => !selectedImages.includes(index));
      
      // Update the gallery with the filtered images
      await galleryApi.update(id, { 
        ...gallery,
        images: updatedImages 
      });
      
      // Reset selection state
      setSelectedImages([]);
      setSelectionMode(false);
      setOpenDeleteImagesModal(false);
      
      // Refresh gallery data
      fetchGallery();
      
      toast.success(t('messages.imagesDeleted'));
    } catch (error) {
      console.error('Error deleting images:', error);
      toast.error(error.message || 'Failed to delete images');
    } finally {
      setLoading(false);
    }
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Skeleton variant="text" height={40} width="30%" sx={{ mb: 2 }} />
          <Skeleton variant="text" height={60} sx={{ mb: 4 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1, mb: 4 }} />
          <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }
  
  if (!gallery) {
    return null;
  }
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        
        {/* Header */}
        <Box sx={{ display: '', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Typography variant="p">
              {format(new Date(gallery.date), 'P')} 
            </Typography>

            <Typography variant="h4" component="h1" gutterBottom>
              {gallery.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {gallery.description}
            </Typography>


          </Box>
          
          <Box>
          
            {user?.isAdmin && (
              <>
                {selectionMode ? (
                  <>
                    <Button  sx={{ mt: 1 }}
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<SelectAllIcon />}
                      onClick={handleSelectAll}
                    >
                      {t('actions.selectAll')}
                    </Button>
                    <Button  sx={{ mt: 1 }}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      startIcon={<ClearAllIcon />}
                      onClick={handleClearSelection}
                    >
                      {t('actions.clearSelection')}
                    </Button>
                    <Button  sx={{ mt: 1 }}
                      variant="outlined"
                      fullWidth
                      onClick={handleToggleSelectionMode}
                    >
                      {t('actions.exitSelectionMode')}
                    </Button>
                    
                    <Button sx={{ mt: 2 }}
                      variant="contained"
                      color="error"
                      fullWidth
                      startIcon={<DeleteIcon />}
                      onClick={handleOpenDeleteImagesModal}
                      disabled={selectedImages.length === 0}
                    >
                      {t('actions.deleteSelected')} ({selectedImages.length})
                    </Button>
                  </>
                ) : (
                  <>
                    <Button  sx={{ mt: 1}}
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<EditIcon />}
                      onClick={handleOpenEditModal}
                    >
                      {t('actions.edit')}
                    </Button>
                    <Button  sx={{ mt: 1 }}
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<SelectAllIcon />}
                      onClick={handleToggleSelectionMode}
                    >
                      {t('actions.selectImages')}
                    </Button>
                    <Button  sx={{ mt: 2 }}
                      variant="outlined"
                      color="error"
                      fullWidth
                      startIcon={<DeleteIcon />}
                      onClick={handleOpenDeleteModal}
                    >
                      {t('actions.delete')}
                    </Button>
                  </>
                )}
              </>
            )}
            </Box>
        </Box>
        
        {/* Image Gallery */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={2}>
            {gallery.images.map((image, index) => (
              <Grid item xs={4} sm={4} md={3} lg={3} key={index}>
                <motion.div variants={itemVariants}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'transform 0.3s',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.02)'
                      },
                      ...(selectionMode && selectedImages.includes(index) && {
                        border: '2px solid',
                        borderColor: 'primary.main',
                      })
                    }}
                    onClick={() => handleOpenImage(index)}
                  >
                    {selectionMode && (
                      <Checkbox
                        checked={selectedImages.includes(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          bgcolor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '50%',
                          padding: '4px',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleImageSelection(index);
                        }}
                      />
                    )}
                    <CardMedia
                      component="img"
                      image={image.thumbnailUrl || getTransformedImageUrl(image.handle || image.url, 'thumbnail')}
                      alt={image.caption || `Image ${index + 1}`}
                      sx={{ objectFit: 'cover' }}
                    />
                    {image.caption && (
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {image.caption}
                        </Typography>
                      </CardContent>
                    )}
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Box>
      
      {/* Image Viewer Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        fullWidth
        onClick={(e) => {
          // Only close if clicking directly on the DialogContent background
          if (e.target === e.currentTarget) {
            handleCloseImageDialog();
          }
        }}
        sx={{ 
          '& .MuiDialog-paper': { 
            overflowY: 'hidden',
            bgcolor: 'black' 
          } 
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          <IconButton
            onClick={handleCloseImageDialog}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              color: 'black',
              zIndex: 1200,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
              boxShadow: '0 0 8px rgba(0, 0, 0, 0.3)',
              padding: '8px',
            }}
            aria-label="close"
            size="large"
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '80vh',
            position: 'relative',
            width: '100%'
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          >
            {/* Left swipe indicator */}
            {swipeDirection === 'left' && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '50%',
                  p: 1,
                  zIndex: 10,
                }}
              >
                <NextIcon fontSize="large" sx={{ color: 'white' }} />
              </Box>
            )}
            
            {/* Right swipe indicator */}
            {swipeDirection === 'right' && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '50%',
                  p: 1,
                  zIndex: 10,
                }}
              >
                <PrevIcon fontSize="large" sx={{ color: 'white' }} />
              </Box>
            )}
            
            <img
              src={gallery.images[selectedImageIndex]?.fullUrl || 
                   getTransformedImageUrl(
                     gallery.images[selectedImageIndex]?.handle || 
                     gallery.images[selectedImageIndex]?.url, 
                     'full'
                   )}
              alt={gallery.images[selectedImageIndex]?.caption || `Image ${selectedImageIndex + 1}`}
              style={{ 
                maxHeight: '100%', 
                maxWidth: '100%', 
                objectFit: 'contain',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'pan-y'
              }}
              draggable="false"
            />
            
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 16,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
                display: { xs: 'none', sm: 'flex' } // Hide on mobile, show on tablet/desktop
              }}
              aria-label="previous image"
            >
              <PrevIcon />
            </IconButton>
            
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 16,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
                display: { xs: 'none', sm: 'flex' } // Hide on mobile, show on tablet/desktop
              }}
              aria-label="next image"
            >
              <NextIcon />
            </IconButton>
          </Box>
          
          {gallery.images[selectedImageIndex]?.caption && (
            <Paper sx={{ p: 2, m: 0, borderRadius: 0 }}>
              <Typography variant="body1">
                {gallery.images[selectedImageIndex].caption}
              </Typography>
            </Paper>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Gallery Modal */}
      {openEditModal && (
        <EditGalleryModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          onSuccess={handleGalleryUpdated}
          gallery={gallery}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <DeleteConfirmationModal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleGalleryDeleted}
          galleryId={gallery._id}
        />
      )}
      
      {/* Delete Selected Images Modal */}
      <Dialog
        open={openDeleteImagesModal}
        onClose={handleCloseDeleteImagesModal}
      >
        <DialogTitle>{t('modals.deleteImages.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('modals.deleteImages.message', { count: selectedImages.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteImagesModal}>
            {t('actions.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteSelectedImages}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GalleryDetailPage; 